// KI-Assistent-Widget im Dashboard (08/2026) — Chat + Eskalations-Übersicht
import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Sparkles, X, Send, FileDown, Loader2, AlertTriangle, Check, Trash2, Wand2, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const SUGGESTIONS = [
  'Wer hat heute abgesagt?',
  'Offene Rechnungen?',
  'Umsatz diesen Monat?',
  'Wo ändere ich die Bonus-Regelung?',
];

const reasonBadge = (esc) => {
  if (esc.type === 'feedback') {
    return { label: '📣 Feedback', cls: 'bg-sky-100 text-sky-700' };
  }
  if (esc.escalation_reason === 'implausible_answer') {
    return { label: 'Unplausible Antwort gestoppt', cls: 'bg-red-100 text-red-700' };
  }
  if ((esc.customer_question || '').startsWith('[Alte Nachricht')) {
    return { label: 'Verspätet zugestellt', cls: 'bg-amber-100 text-amber-700' };
  }
  return { label: 'KI wusste keine Antwort', cls: 'bg-blue-100 text-blue-700' };
};

const EscalationCard = ({ esc, onDone, index }) => {
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState(false);
  const badge = reasonBadge(esc);

  const reply = async () => {
    if (!answer.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/chatbot/escalations/${esc.id}/reply`, { answer: answer.trim() }, { headers: authHeaders() });
      toast.success('Antwort an den Kunden gesendet ✅');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Antwort fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const dismiss = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/chatbot/escalations/${esc.id}/dismiss`, {}, { headers: authHeaders() });
      toast.success('Eskalation verworfen');
      onDone();
    } catch (e) {
      toast.error('Verwerfen fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`escalation-card-${index}`} className="bg-white border border-gray-200 rounded-xl p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-gray-800 truncate">
          {esc.customer_name || esc.customer_phone || 'Unbekannt'}
        </span>
        <span className={`shrink-0 text-[11px] px-2 py-0.5 rounded-full font-medium ${badge.cls}`}>{badge.label}</span>
      </div>
      <p className="text-sm text-gray-700 whitespace-pre-wrap">
        {esc.type === 'feedback' ? '💬' : '❓'} {esc.customer_question}
      </p>
      {esc.type === 'feedback' && esc.summary && (
        <p className="text-xs text-sky-700">🔎 {esc.summary}</p>
      )}
      {esc.type === 'feedback' && Array.isArray(esc.followups) && esc.followups.length > 0 && (
        <div className="space-y-0.5">
          {esc.followups.map((f, k) => (
            <p key={k} className="text-[11px] text-gray-500 pl-2 border-l-2 border-sky-200">↪ {f}</p>
          ))}
        </div>
      )}
      {esc.suppressed_answer && (
        <div className="text-xs bg-red-50 border border-red-100 rounded-lg p-2 text-gray-600">
          <span className="font-medium text-red-700">Bot wollte antworten:</span>
          <p className="mt-0.5 whitespace-pre-wrap">{esc.suppressed_answer}</p>
          <button
            data-testid={`escalation-adopt-btn-${index}`}
            onClick={() => setAnswer(esc.suppressed_answer)}
            className="mt-1.5 flex items-center gap-1 text-indigo-600 font-medium hover:underline"
          >
            <Wand2 className="w-3.5 h-3.5" /> Vorschlag übernehmen
          </button>
        </div>
      )}
      <textarea
        data-testid={`escalation-answer-input-${index}`}
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        placeholder="Deine Antwort an den Kunden…"
        rows={2}
        className="w-full text-sm border border-gray-300 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
      />
      <div className="flex items-center gap-2">
        <button
          data-testid={`escalation-reply-btn-${index}`}
          onClick={reply}
          disabled={busy || !answer.trim()}
          className="flex-1 flex items-center justify-center gap-1.5 text-sm bg-indigo-600 text-white rounded-lg py-1.5 disabled:opacity-40 hover:bg-indigo-700"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Antworten
        </button>
        <button
          data-testid={`escalation-dismiss-btn-${index}`}
          onClick={dismiss}
          disabled={busy}
          className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50"
          title="Verwerfen"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <p className="text-[11px] text-gray-400">
        Der Bot poliert deine Antwort, sendet sie dem Kunden und lernt sie für die Zukunft. 🧠
      </p>
    </div>
  );
};

const AdminAiWidget = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [tab, setTab] = useState('chat');
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [escalations, setEscalations] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [fbCount, setFbCount] = useState(0);
  const [escType, setEscType] = useState('all');
  const [messages, setMessages] = useState([
    { role: 'bot', text: 'Hallo! 👋 Ich bin dein Studio-Assistent. Frag mich z.B. "Wer hat heute abgesagt?", "Wann hat [Kunde] Training?" oder "Wo ändere ich die Bonus-Regelung?"' },
  ]);
  const bottomRef = useRef(null);

  const loadEscalations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chatbot/escalations?status=open&type=${escType}&limit=20`, { headers: authHeaders() });
      setEscalations(res.data.escalations || []);
      setOpenCount(res.data.counts?.open || 0);
      setFbCount(res.data.counts?.open_feedback || 0);
    } catch (e) { /* still zeigen */ }
  }, [escType]);

  useEffect(() => { loadEscalations(); }, [loadEscalations]);
  useEffect(() => { if (open) loadEscalations(); }, [open, loadEscalations]);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open, tab]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text: msg }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/settings/admin-ai/chat`, { message: msg }, { headers: authHeaders() });
      setMessages((m) => [...m, {
        role: 'bot',
        text: res.data.reply,
        documentUrl: res.data.document_url,
        filename: res.data.filename,
        link: res.data.link,
        linkLabel: res.data.link_label,
      }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'bot', text: '❌ Fehler bei der Anfrage. Bitte nochmal versuchen.' }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          data-testid="admin-ai-widget-open"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow-lg hover:shadow-xl hover:scale-105 transition-transform"
        >
          <Sparkles className="w-5 h-5" />
          <span className="font-medium text-sm">KI-Assistent</span>
          {openCount > 0 && (
            <span data-testid="admin-ai-widget-badge" className="ml-1 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
              {openCount}
            </span>
          )}
        </button>
      )}
      {open && (
        <div
          data-testid="admin-ai-widget-panel"
          className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[540px] max-h-[calc(100vh-6rem)] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-indigo-600 to-violet-600 text-white">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              <span className="font-semibold">Studio KI-Assistent</span>
            </div>
            <button data-testid="admin-ai-widget-close" onClick={() => setOpen(false)} className="p-1 rounded hover:bg-white/20">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex border-b bg-white">
            <button
              data-testid="admin-ai-tab-chat"
              onClick={() => setTab('chat')}
              className={`flex-1 py-2 text-sm font-medium border-b-2 ${tab === 'chat' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              Chat
            </button>
            <button
              data-testid="admin-ai-tab-escalations"
              onClick={() => { setTab('escalations'); loadEscalations(); }}
              className={`flex-1 py-2 text-sm font-medium border-b-2 flex items-center justify-center gap-1.5 ${tab === 'escalations' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              <AlertTriangle className="w-4 h-4" /> Eskalationen
              {openCount > 0 && (
                <span className="bg-red-500 text-white text-[11px] font-bold px-1.5 py-0.5 rounded-full">{openCount}</span>
              )}
            </button>
          </div>

          {tab === 'chat' && (
            <>
              <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-gray-50">
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${
                      m.role === 'user' ? 'bg-indigo-600 text-white rounded-br-sm' : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm'
                    }`}>
                      {m.text}
                      {m.documentUrl && (
                        <a
                          href={m.documentUrl}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`admin-ai-doc-link-${i}`}
                          className="mt-2 flex items-center gap-1.5 text-indigo-600 font-medium hover:underline"
                        >
                          <FileDown className="w-4 h-4" /> {m.filename || 'Dokument öffnen'}
                        </a>
                      )}
                      {m.link && (
                        <button
                          onClick={() => { navigate(m.link); setOpen(false); }}
                          data-testid={`admin-ai-nav-link-${i}`}
                          className="mt-2 flex items-center gap-1.5 text-indigo-600 font-medium hover:underline"
                        >
                          <ArrowRight className="w-4 h-4" /> {m.linkLabel || 'Zur Seite'}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex items-center gap-2 text-gray-500 text-sm px-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> denkt nach…
                  </div>
                )}
                <div ref={bottomRef} />
              </div>
              {messages.length <= 1 && (
                <div className="px-3 pb-1 flex flex-wrap gap-1.5">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      className="text-xs px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-100"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
              <div className="p-3 border-t bg-white flex items-center gap-2">
                <input
                  data-testid="admin-ai-widget-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && send()}
                  placeholder="Frage stellen…"
                  className="flex-1 px-3 py-2 rounded-full border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <button
                  data-testid="admin-ai-widget-send"
                  onClick={() => send()}
                  disabled={loading || !input.trim()}
                  className="p-2.5 rounded-full bg-indigo-600 text-white disabled:opacity-40 hover:bg-indigo-700"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </>
          )}

          {tab === 'escalations' && (
            <div className="flex flex-col flex-1 min-h-0">
              <div className="flex gap-1.5 px-3 pt-2 pb-1 bg-white border-b">
                {[['all', 'Alle'], ['feedback', `📣 Feedback${fbCount ? ` (${fbCount})` : ''}`], ['question', '❓ Fragen']].map(([k, l]) => (
                  <button
                    key={k}
                    data-testid={`admin-ai-esc-type-${k}`}
                    onClick={() => setEscType(k)}
                    className={`text-xs px-2.5 py-1 rounded-full border ${escType === k ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
              <div data-testid="admin-ai-escalations-list" className="flex-1 overflow-y-auto p-3 space-y-3 bg-gray-50">
                {escalations.length === 0 ? (
                  <div className="text-center text-gray-500 text-sm pt-10">
                    {escType === 'feedback'
                      ? '📣 Kein offenes Feedback.'
                      : '✅ Keine offenen Eskalationen.'}
                    <br />Der Bot hat alles im Griff!
                  </div>
                ) : (
                  escalations.map((esc, i) => (
                    <EscalationCard key={esc.id} esc={esc} index={i} onDone={loadEscalations} />
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default AdminAiWidget;
